// Navigation by role (pure, unit-tested). Paths are app-relative ("/en/courses"): next/link adds the base path;
// only the back-office link is a raw <a> and therefore gets the base path here.
export type NavRole = 'superAdmin' | 'editor' | 'orgAdmin' | 'student' | 'freeUser' | 'worker'
export type NavKey = 'dashboard' | 'courses' | 'news' | 'training' | 'mockExams' | 'exams' | 'certification' | 'org' | 'orgLive' | 'live' | 'accountTypes' | 'admin'
export type NavItem = { key: NavKey; href: string; external?: boolean }

const paths: Record<Exclude<NavKey, 'admin'>, string> = {
  dashboard: '/dashboard',
  courses: '/courses',
  news: '/news',
  training: '/training',
  mockExams: '/mock-exams',
  exams: '/exams',
  certification: '/certification',
  org: '/org',
  orgLive: '/org/live',
  live: '/live',
  accountTypes: '/account-types',
}

const publicKeys: NavKey[] = ['courses', 'training', 'accountTypes']
const learnerKeys: NavKey[] = ['dashboard', 'courses', 'training', 'mockExams', 'exams', 'certification']

export const isStaffRole = (role?: string | null) => role === 'superAdmin' || role === 'editor'

// What a learner account can use: full access (school token, certification account, admin) and certification tracks.
export type NavAccess = { fullAccess?: boolean; certification?: boolean }

// Free accounts (owner, 27/09: « accès uniquement aux exercices/cours pour compte gratuit » ; « Examen de certification
// pour un compte gratuit ? Ça n'existe pas ») have no exams nor certification. School students, certification accounts
// (paid, F11 08/10) and accounts given full access keep them.
export const hasExamsArea = (role: NavRole | string | null | undefined, access: NavAccess = {}) =>
  role !== 'freeUser' || Boolean(access.fullAccess || access.certification)

// News (F07) comes right after the courses, for everyone.
const withNews = (keys: NavKey[]): NavKey[] => keys.flatMap((key): NavKey[] => (key === 'courses' ? ['courses', 'news'] : [key]))

export function navKeysForRole(role?: NavRole | string | null, access: NavAccess = {}): NavKey[] {
  return withNews(baseKeysForRole(role, access))
}

function baseKeysForRole(role: NavRole | string | null | undefined, access: NavAccess): NavKey[] {
  if (!role) return publicKeys
  // Teachers: their exams live in the organisation space; learner "Exams" and "Certification" only crowded the menu.
  if (role === 'orgAdmin') return [...learnerKeys.filter((key) => key !== 'exams' && key !== 'certification'), 'org', 'orgLive']
  if (isStaffRole(role)) return [...learnerKeys, 'admin']
  // Organisation students join the teacher's live classes (F17).
  if (role === 'student') return [...learnerKeys, 'live']
  // Free-role accounts: mock exams with full access only (F16); exams and certification as said above.
  return learnerKeys.filter((key) => (key === 'mockExams' ? Boolean(access.fullAccess) : key === 'exams' || key === 'certification' ? hasExamsArea(role, access) : true))
}

export function buildNav(role: NavRole | string | null | undefined, locale: string, basePath = '', access: NavAccess = {}): NavItem[] {
  return navKeysForRole(role, access).map((key) =>
    key === 'admin' ? { key, href: `${basePath}/admin`, external: true } : { key, href: `/${locale}${paths[key]}` },
  )
}

// Active item: exact match or a sub-page; "/org" must not light up on "/org/live".
export function activeNavKey(items: NavItem[], pathname: string): NavKey | null {
  let best: NavItem | null = null
  for (const item of items) {
    if (item.external) continue
    if (pathname === item.href || pathname.startsWith(`${item.href}/`)) {
      if (!best || item.href.length > best.href.length) best = item
    }
  }
  return best?.key ?? null
}

// Same page in the other locale, keeping the rest of the path and the query string.
export function switchLocalePath(pathname: string, target: string, search = ''): string {
  const parts = pathname.split('/')
  if (parts[1] === 'en' || parts[1] === 'fr') parts[1] = target
  else parts.splice(1, 0, target)
  const path = parts.join('/').replace(/\/$/, '') || `/${target}`
  return search ? `${path}${search.startsWith('?') ? search : `?${search}`}` : path
}

// CMS links are stored without locale ("/courses", "https://…", "mailto:…").
export function localizeHref(href: string, locale: string): { href: string; external: boolean } {
  if (/^([a-z][a-z0-9+.-]*:|#)/i.test(href)) return { href, external: !href.startsWith('#') }
  if (!href.startsWith('/')) return { href: `/${locale}/${href}`, external: false }
  if (/^\/(en|fr)(\/|$)/.test(href)) return { href, external: false }
  return { href: `/${locale}${href === '/' ? '' : href}`, external: false }
}
